<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/salary-log';
    import { computed } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import PageCard from '@/components/PageCard.vue';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem } from '@/types';
    import { useI18n } from 'vue-i18n';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    const selectedCompanyName = computed(() => page.props.auth.user.selected_company_name);

    const { t } = useI18n();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'menu.payroll_activity_log',
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.SALARY_LOG, selectedCompanyId],
        queryFn: getSalaryLog,
        enabled: computed(() => !!page.props.auth.user.selected_company_id),
    });

    async function getSalaryLog() {
        const { data } = await axios.get(route('v2.salary.log.table'));
        const rows = data?.data ?? [];
        return rows
            .slice()
            .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
</script>

<template>
    <Head :title="t('payrolls.activity_log.header', { company: selectedCompanyName })" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.SALARY_LOG"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :manual-sorting="true"
                :default-sorting="[
                    {
                        id: 'date',
                        desc: true,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ t('payrolls.activity_log.header', { company: selectedCompanyName }) }}
                    </CardTitle>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
